/* global fetch, window, setTimeout */
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import LoadingButton from '../../fw/LoadingButton.react';
import AppPanel from '../../fw/AppPanel.react';
import Table from 'react-bootstrap/lib/Table';
import './OverdueBillsList.scss';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import EnqueueOverdueBillsFetchJob from '../EnqueueOverdueBillsFetchJob.react';
import EnqueueOverdueBillsNotifyJob from './EnqueueOverdueBillsNotifyJob.react';
import DeleteOrganisationOverdueBill from '../DeleteOrganisationOverdueBill.react';
import UpdateOrganisationOverdueBillIgnore from './UpdateOrganisationOverdueBillIgnore.react.js';
import Relay from 'react-relay';
import showNotification, {showJobNotification} from '../../app/notification.js';
// quick action removed; using rules-based notify job instead
import DownloadOrganisationOverdueBillDocument from './DownloadOrganisationOverdueBillDocument.react.js';

import * as BrowserPermission from '../../BrowserPermission';
import * as Permission from '../../../common/util/Permission';
const messages = defineMessages({
    title: {
        id: 'overdueBills.title',
        defaultMessage: 'Overdue Invoices'
    },
    loadFromFlexiOrg: {
        id: 'overdueBills.loadFromFlexiOrg',
        defaultMessage: 'Load invoices (this org.)'
    },
    loadFromFlexiAll: {
        id: 'overdueBills.loadFromFlexiAll',
        defaultMessage: 'Load invoices (all org.)'
    },
    columnCode: {
        id: 'overdueBills.column.code',
        defaultMessage: 'Number'
    },
    columnVariableSymbol: {
        id: 'overdueBills.column.varSym',
        defaultMessage: 'Variable symbol'
    },
    columnDueDate: {
        id: 'overdueBills.column.dueDate',
        defaultMessage: 'Due date'
    },
    columnTotalAmount: {
        id: 'overdueBills.column.totalAmount',
        defaultMessage: 'Total amount'
    },
    columnAmountToPay: {
        id: 'overdueBills.column.amountToPay',
        defaultMessage: 'Amount to pay'
    },
    columnCurrency: {
        id: 'overdueBills.column.currency',
        defaultMessage: 'Currency'
    },
    columnDaysOverdue: {
        id: 'overdueBills.column.daysOverdue',
        defaultMessage: 'Days overdue'
    },
    columnDocuments: {
        id: 'overdueBills.column.documents',
        defaultMessage: 'Documents'
    },
    columnActions: {
        id: 'overdueBills.column.actions',
        defaultMessage: 'Actions'
    },
	columnSelect: {
		id: 'overdueBills.column.select',
		defaultMessage: 'Select'
	},
    filterOnlyUnpaidOverdue: {
        id: 'overdueBills.filter.onlyUnpaidOverdue',
        defaultMessage: 'Only unpaid and overdue'
    },
    pageSizeLabel: {
        id: 'overdueBills.pageSize',
        defaultMessage: 'Page size'
    },
    confirmNotifyOrg: {
        id: 'overdueBills.confirm.notifyOrg',
        defaultMessage: 'Are you sure you want to run rules for this organisation?'
    },
    confirmNotifyAll: {
        id: 'overdueBills.confirm.notifyAll',
        defaultMessage: 'Are you sure you want to run rules for all organisations?'
	},
	deleteSelected: {
		id: 'overdueBills.deleteSelected',
		defaultMessage: 'Delete selected'
	},
	confirmDeleteSelected: {
		id: 'overdueBills.confirm.deleteSelected',
		defaultMessage: 'Delete selected invoices?'
	},
	ignoreSelected: {
		id: 'overdueBills.ignoreSelected',
		defaultMessage: 'Ignore selected'
	},
	confirmIgnoreSelected: {
		id: 'overdueBills.confirm.ignoreSelected',
		defaultMessage: 'Ignore selected invoices in rules?'
	},
	unignoreSelected: {
		id: 'overdueBills.unignoreSelected',
		defaultMessage: 'Unignore selected'
	},
	confirmUnignoreSelected: {
		id: 'overdueBills.confirm.unignoreSelected',
		defaultMessage: 'Remove ignore flag for selected invoices?'
	}
});

class OverdueBillsList extends React.Component {
    static propTypes = {
        intl: PropTypes.object,
        onRefresh: PropTypes.func,
        onSelect: PropTypes.func,
        organisationId: PropTypes.string,
        user: PropTypes.object,
    };
    constructor(props) {
        super(props);
        this.state = {
            items: [], // TODO: connect to GraphQL/DAO
            loading: false,
            error: null,
            selected: null,
			selectedIds: {},
            debug: null,
            notice: null,
            ignoredById: {},
            onlyUnpaidOverdue: true,
			currentPage: 1,
			pageSize: 50,
        };
    }

    // quickAction removed

    resolveOrganisationId() {
        if (this.props && this.props.organisationId) return this.props.organisationId;
        try {
            const m = (typeof window !== 'undefined' && window.location && window.location.pathname || '').match(/\/organisations\/([0-9a-fA-F-]{36})\//);
            if (m && m[1]) return m[1];
        } catch (e) {}
        return null;
    }

    componentDidMount() {
        this.load();
    }

    async load() {
        // no-op: data will be provided via Relay parent
        return;
    }

    async waitForJobFinished(jobId, { intervalMs = 1000, attempts = 20 } = {}) {
        if (!jobId) return false;
        const endpoint = '/?locale=cs';
        const query = `query Q($jobId: ID!) { user { job(id: $jobId) { status } } }`;
        for (let i = 0; i < attempts; i++) {
            try {
                const resp = await fetch(endpoint, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ query, variables: { jobId } }),
                });
                const json = await resp.json();
                const status = json && json.data && json.data.user && json.data.user.job && json.data.user.job.status;
                if (status === 'finished' || status === 'failed' || status === 'skipped') return true;
            } catch (e) { /* ignore and retry */ }
            await new Promise((resolve) => setTimeout(resolve, intervalMs));
        }
        return false;
    }

    async handleDelete(item) {
        const id = item && item.id;
        if (!id) return;
        const code = item && item.code ? String(item.code) : '';
        if (!window.confirm(`Opravdu smazat doklad ${code || id}?`)) return;
        await new Promise((resolve, reject) => {
            try {
                Relay.Store.commitUpdate(new DeleteOrganisationOverdueBill({ id }), {
                    onSuccess: () => resolve(null),
                    onFailure: (_, err) => reject(err),
                });
            } catch (e) { reject(e); }
        });
        // After deletion, request parent (Relay) refresh via props.onRefresh if it exists
        if (this.props.onRefresh) this.props.onRefresh();
    }

	handleToggleSelect(id, checked) {
		this.setState((prev) => ({
			selectedIds: { ...prev.selectedIds, [id]: !!checked }
		}));
	}

	handleToggleSelectAll(items, checked) {
		const next = {};
		if (checked) {
			for (const it of items) next[it.id] = true;
		}
		this.setState({ selectedIds: next });
	}

	async handleBulkDelete(items) {
		const ids = items.filter((it) => this.state.selectedIds[it.id]).map((it) => it.id);
		if (ids.length === 0) return;
		const msg = this.props.intl ? this.props.intl.formatMessage(messages.confirmDeleteSelected) : 'Delete selected invoices?';
		if (!window.confirm(msg)) return;
		for (const id of ids) {
			// eslint-disable-next-line no-await-in-loop
			await new Promise((resolve) => {
				try {
					Relay.Store.commitUpdate(new DeleteOrganisationOverdueBill({ id }), {
						onSuccess: () => resolve(null),
						onFailure: () => resolve(null),
					});
				} catch (e) { resolve(null); }
			});
		}
		this.setState({ selectedIds: {} });
		if (this.props.onRefresh) this.props.onRefresh();
	}

	async handleBulkUnignore(items) {
		const ids = items.filter((it) => this.state.selectedIds[it.id]).map((it) => it.id);
		if (ids.length === 0) return;
		const msg = this.props.intl ? this.props.intl.formatMessage(messages.confirmUnignoreSelected) : 'Remove ignore flag for selected invoices?';
		if (!window.confirm(msg)) return;
		// optimistic UI
		this.setState((prev) => {
			const next = { ...prev.ignoredById };
			for (const id of ids) next[id] = false;
			return { ignoredById: next };
		});
		for (const id of ids) {
			// eslint-disable-next-line no-await-in-loop
			await new Promise((resolve) => {
				try {
					Relay.Store.commitUpdate(new UpdateOrganisationOverdueBillIgnore({ id, isIgnored: false }), {
						onSuccess: () => resolve(null),
						onFailure: () => resolve(null),
					});
				} catch (e) { resolve(null); }
			});
		}
		this.setState({ selectedIds: {} });
		if (this.props.onRefresh) this.props.onRefresh();
	}

	async handleBulkIgnore(items) {
		const ids = items.filter((it) => this.state.selectedIds[it.id]).map((it) => it.id);
		if (ids.length === 0) return;
		const msg = this.props.intl ? this.props.intl.formatMessage(messages.confirmIgnoreSelected) : 'Ignore selected invoices in rules?';
		if (!window.confirm(msg)) return;
		// optimistic UI
		this.setState((prev) => {
			const next = { ...prev.ignoredById };
			for (const id of ids) next[id] = true;
			return { ignoredById: next };
		});
		for (const id of ids) {
			// eslint-disable-next-line no-await-in-loop
			await new Promise((resolve) => {
				try {
					Relay.Store.commitUpdate(new UpdateOrganisationOverdueBillIgnore({ id, isIgnored: true }), {
						onSuccess: () => resolve(null),
						onFailure: () => resolve(null),
					});
				} catch (e) { resolve(null); }
			});
		}
		this.setState({ selectedIds: {} });
		if (this.props.onRefresh) this.props.onRefresh();
	}

    renderRow(item) {
        const toText = (v) => {
            if (v == null) return '';
            if (typeof v === 'object') return JSON.stringify(v);
            return String(v);
        };
        const handleDownload = (format) => {
            try {
                Relay.Store.commitUpdate(new DownloadOrganisationOverdueBillDocument({ id: item.id, format }), {
                    onSuccess: (res) => {
                        try {
                            const url = res && res.downloadOrganisationOverdueBillDocument && res.downloadOrganisationOverdueBillDocument.url;
                            if (url) {
                                window.open(url, '_blank');
                            } else {
                                showNotification('Download', 'Download failed (no URL returned).');
                            }
                        } catch (e) {
                            showNotification('Download', 'Download failed.');
                        }
                    },
                    onFailure: () => {
                        showNotification('Download', 'Download failed.');
                    },
                });
            } catch (e) {
                showNotification('Download', 'Download failed.');
            }
        };
        let parsedDate;
        if (item.dueDate && typeof item.dueDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(item.dueDate)) {
            parsedDate = moment(item.dueDate, 'YYYY-MM-DD');
        } else if (item.dueDate) {
            parsedDate = moment(item.dueDate);
        }
        const daysOverdue = parsedDate && parsedDate.isValid() ? moment().diff(parsedDate, 'days') : undefined;
        // determine developer visibility
        const organisationIdForRow = this.resolveOrganisationId();
        const isDeveloperForRow = organisationIdForRow
            ? (BrowserPermission.hasOrganisationPermission(Permission.PERM_DEVELOPER, organisationIdForRow) && BrowserPermission.developerModeEnabled(organisationIdForRow))
            : false;
        const isIgnored = this.state.ignoredById[item.id] != null ? !!this.state.ignoredById[item.id] : !!item.isIgnored;
        const toNumberOrNull = (v) => {
            if (v === null || v === undefined) return null;
            const n = Number(v);
            return Number.isNaN(n) ? null : n;
        };
        const amountToPayNum = toNumberOrNull(item.amountToPay);
        const totalAmountNum = toNumberOrNull(item.totalAmount);
        const isPaid = amountToPayNum != null ? amountToPayNum <= 0 : false;
        const isPartial = amountToPayNum != null && totalAmountNum != null ? (amountToPayNum > 0 && amountToPayNum < totalAmountNum) : false;
        const isOverdueUnpaid = amountToPayNum != null ? (amountToPayNum > 0 && (daysOverdue != null && daysOverdue > 0)) : false;
        let statusClass = '';
        if (isPaid) statusClass = 'status-paid';
        else if (isPartial) statusClass = 'status-partial';
        else if (isOverdueUnpaid) statusClass = 'status-overdue';
		return (
			<tr className={`clickable-row ${statusClass}`} key={String(item.id || item.code || Math.random())} onClick={() => {
                if (this.props.onSelect) {
                    this.props.onSelect(item.__expanded || item);
                } else {
                    this.setState({ selected: (item.__expanded || item) });
                }
			}} role="button" style={isIgnored && isDeveloperForRow ? { opacity: 0.6 } : undefined} title="Zobrazit detail">
				<td onClick={(e) => e.stopPropagation()} style={{width: 28, textAlign: 'center'}}>
					<input
						checked={!!this.state.selectedIds[item.id]}
						onChange={(e) => this.handleToggleSelect(item.id, !!e.target.checked)}
						type="checkbox"
					/>
				</td>
                <td>
                    <div>{toText(item.code)}</div>
                    {isIgnored && isDeveloperForRow ? (
                        <div><span className="label label-default">Ignored</span></div>
                    ) : null}
                </td>
                <td>{toText(item.variableSymbol)}</td>
                <td>{parsedDate && parsedDate.isValid() ? parsedDate.format('DD.MM.YYYY') : ''}</td>
                <td style={{textAlign:'right'}}>{toText(item.totalAmount)}</td>
                <td style={{textAlign:'right'}}>{toText(item.amountToPay)}</td>
                <td>{toText(item.currencyCode)}</td>
                <td>{daysOverdue != null ? String(daysOverdue) : ''}</td>
                <td onClick={(e) => e.stopPropagation()}>
                    <button
                        className="btn btn-default btn-xs"
                        onClick={(e) => { e.stopPropagation(); handleDownload('pdf'); }}
                        style={{ marginRight: 6 }}
                        title="PDF"
                        type="button"
                    >
                        <span aria-hidden="true" className="fa fa-file-pdf-o"></span>
                    </button>
                    <button
                        className="btn btn-default btn-xs"
                        onClick={(e) => { e.stopPropagation(); handleDownload('isdoc'); }}
                        title="ISDOC"
                        type="button"
                    >
                        <span aria-hidden="true" className="fa fa-file-code-o"></span>
                    </button>
                </td>
            </tr>
        );
    }

    render() {
        const { loading, error } = this.state;
        // items z Relay parenta (OverdueBillsPage):
        let items = [];
        const organisationId = this.resolveOrganisationId();
        const isDeveloper = organisationId
            ? (BrowserPermission.hasOrganisationPermission(Permission.PERM_DEVELOPER, organisationId) && BrowserPermission.developerModeEnabled(organisationId))
            : false;
        try {
            const edges = this.props.user && this.props.user.organisation && this.props.user.organisation.organisationOverdueBills && this.props.user.organisation.organisationOverdueBills.edges;
            const rawItems = Array.isArray(edges) ? edges.map(e => e && e.node).filter(Boolean) : [];
            items = rawItems.map((b, idx) => {
                const code = String(b.code || '');
                let dueDate = b.dueDate || null;
                if (typeof dueDate === 'string') {
                    if (dueDate.indexOf('T') !== -1) dueDate = dueDate.split('T')[0];
                    if (dueDate.indexOf('+') !== -1) dueDate = dueDate.split('+')[0];
                    if (dueDate.indexOf(' ') !== -1) dueDate = dueDate.split(' ')[0];
                }
                const amountToPay = String(b.amountToPay != null ? b.amountToPay : '');
                const totalAmount = String(b.totalAmount != null ? b.totalAmount : '');
                const id = String(b.id || code || idx);
                const realId = b.realId ? String(b.realId) : '';
                const isIgnored = !!b.isIgnored;
                let variableSymbol = String(b.variableSymbol || '');
                let currencyCode = String(b.currencyCode || '');
                if (currencyCode && currencyCode.toLowerCase().indexOf('code:') === 0) {
                    currencyCode = currencyCode.substring(5).trim();
                }
                const __expanded = { id, realId, code, variableSymbol, dueDate, amountToPay, totalAmount, currencyCode, isIgnored };
                return { id, realId, code, variableSymbol, dueDate, amountToPay, totalAmount, currencyCode, isIgnored, __raw: b, __expanded };
            });
            // filter: only unpaid and overdue (amountToPay > 0 and dueDate < today)
            if (this.state.onlyUnpaidOverdue) {
                const now = new Date();
                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                items = items.filter((i) => {
                    const toPay = Number(i.amountToPay || 0);
                    const d = i.dueDate ? new Date(i.dueDate) : null;
                    const overdue = d ? (d.getTime() < today.getTime()) : false;
                    return toPay > 0 && overdue;
                });
            }
            // řadit od nejmladší po nejstarší (newest first) podle dueDate
            items.sort((a, b) => {
                const ad = a.dueDate ? new Date(a.dueDate).getTime() : 0;
                const bd = b.dueDate ? new Date(b.dueDate).getTime() : 0;
                return bd - ad;
            });
        } catch (e) {}
        // Pagination
        const totalItems = items.length;
        const pageSize = this.state.pageSize || 50;
        const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
        let currentPage = this.state.currentPage || 1;
        if (currentPage > totalPages) currentPage = totalPages;
        if (currentPage < 1) currentPage = 1;
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = Math.min(startIndex + pageSize, totalItems);
        const visibleItems = items.slice(startIndex, endIndex);
        const selectedCount = Object.keys(this.state.selectedIds).filter(k => this.state.selectedIds[k]).length;
        return (
            <AppPanel title={this.props.intl ? this.props.intl.formatMessage(messages.title) : 'Overdue Invoices'}>
                <div className="panel-body">
                    <div className="row">
                        <div className="col-md-12">
                            <div className="pull-right" style={{ marginBottom: '20px' }}>
                                {isDeveloper ? (
                                <span>
                                <LoadingButton className="btn btn-default" onClick={async () => {
                                    this.setState({ loading: true, error: null });
                                    try {
                                        this.setState({ notice: { type: 'info', message: this.props.intl ? this.props.intl.formatMessage({ id: 'overdueBills.jobStarting', defaultMessage: 'Starting job…' }) : 'Starting job…' } });
                                        // Enqueue job via GraphQL (Relay) for THIS organisation
                                        let enqueuedJobId = null;
                                        await new Promise((resolve, reject) => {
                                            try {
                                                Relay.Store.commitUpdate(
                                                    new EnqueueOverdueBillsFetchJob({ organisationId: this.resolveOrganisationId() }),
                                                    { onSuccess: (response) => {
                                                        try {
                                                            const job = response && response.enqueueOverdueBillsFetchJob && response.enqueueOverdueBillsFetchJob.job;
                                                            if (job) {
                                                                showJobNotification(this.props.intl, job, this.props.intl ? this.props.intl.formatMessage({ id: 'overdueBills.jobStarted', defaultMessage: 'Fetching overdue invoices…' }) : 'Fetching overdue invoices…');
                                                                this.setState({ notice: { type: 'success', message: (this.props.intl ? this.props.intl.formatMessage({ id: 'overdueBills.jobEnqueued', defaultMessage: 'Job enqueued' }) : 'Job enqueued') + ` (id: ${job.realId || job.id})` } });
                                                                enqueuedJobId = job.realId || job.id;
                                                            } else {
                                                                showNotification('Job enqueue', 'Job created');
                                                            }
                                                        } catch (e) {}
                                                        resolve(null);
                                                    }, onFailure: (_, err) => reject(err) }
                                                );
                                            } catch (e) { reject(e); }
                                        });
                                        // Počkej na dokončení jobu a obnov seznam jednou
                                        if (enqueuedJobId) {
                                            await this.waitForJobFinished(enqueuedJobId);
                                        }
                                        if (this.props.onRefresh) {
                                            this.props.onRefresh();
                                        } else {
                                            await this.load();
                                        }
                                    } catch (e) {
                                        this.setState({ error: e && e.message ? e.message : String(e) });
                                    } finally {
                                        this.setState({ loading: false });
                                    }
                                }}>
                                    <FormattedMessage {...messages.loadFromFlexiOrg} />
                                </LoadingButton>
                                {' '}
                                <LoadingButton className="btn btn-default" onClick={async () => {
                                    this.setState({ loading: true, error: null });
                                    try {
                                        this.setState({ notice: { type: 'info', message: this.props.intl ? this.props.intl.formatMessage({ id: 'overdueBills.jobStarting', defaultMessage: 'Starting job…' }) : 'Starting job…' } });
                                        // Enqueue job via GraphQL (Relay) for ALL organisations
                                        let enqueuedJobId = null;
                                        await new Promise((resolve, reject) => {
                                            try {
                                                Relay.Store.commitUpdate(
                                                    new EnqueueOverdueBillsFetchJob({}),
                                                    { onSuccess: (response) => {
                                                        try {
                                                            const job = response && response.enqueueOverdueBillsFetchJob && response.enqueueOverdueBillsFetchJob.job;
                                                            if (job) {
                                                                showJobNotification(this.props.intl, job, this.props.intl ? this.props.intl.formatMessage({ id: 'overdueBills.jobStarted', defaultMessage: 'Fetching overdue invoices…' }) : 'Fetching overdue invoices…');
                                                                this.setState({ notice: { type: 'success', message: (this.props.intl ? this.props.intl.formatMessage({ id: 'overdueBills.jobEnqueued', defaultMessage: 'Job enqueued' }) : 'Job enqueued') + ` (id: ${job.realId || job.id})` } });
                                                                enqueuedJobId = job.realId || job.id;
                                                            } else {
                                                                showNotification('Job enqueue', 'Job created');
                                                            }
                                                        } catch (e) {}
                                                        resolve(null);
                                                    }, onFailure: (_, err) => reject(err) }
                                                );
                                            } catch (e) { reject(e); }
                                        });
                                        // Počkej na dokončení jobu a obnov seznam jednou
                                        if (enqueuedJobId) {
                                            await this.waitForJobFinished(enqueuedJobId);
                                        }
                                        if (this.props.onRefresh) {
                                            this.props.onRefresh();
                                        } else {
                                            await this.load();
                                        }
                                    } catch (e) {
                                        this.setState({ error: e && e.message ? e.message : String(e) });
                                    } finally {
                                        this.setState({ loading: false });
                                    }
                                }}>
                                    <FormattedMessage {...messages.loadFromFlexiAll} />
                                </LoadingButton>
                                {' '}
                                <LoadingButton className="btn btn-default" onClick={async () => {
                                    const msg = this.props.intl ? this.props.intl.formatMessage(messages.confirmNotifyOrg) : 'Are you sure you want to run rules for this organisation?';
                                    if (!window.confirm(msg)) return;
                                    // Enqueue notify (current organisation)
                                    try {
                                        await new Promise((resolve, reject) => {
                                            try {
                                                Relay.Store.commitUpdate(
                                                    new EnqueueOverdueBillsNotifyJob({ organisationId: this.resolveOrganisationId() }),
                                                    { onFailure: (_, err) => reject(err), onSuccess: (response) => {
                                                        try {
                                                            const job = response && response.enqueueOverdueBillsNotifyJob && response.enqueueOverdueBillsNotifyJob.job;
                                                            if (job) showJobNotification(this.props.intl, job, 'Running notify for organisation…');
                                                        } catch (e) {}
                                                        resolve(null);
                                                    } }
                                                );
                                            } catch (e) { reject(e); }
                                        });
                                    } catch (e) { showNotification('Chyba', String(e && e.message ? e.message : e)); }
                                }}>
                                    <FormattedMessage defaultMessage="Run rules (this org.)" id="overdueBills.notifyOrg" />
                                </LoadingButton>
                                {' '}
                                <LoadingButton className="btn btn-default" onClick={async () => {
                                    const msg = this.props.intl ? this.props.intl.formatMessage(messages.confirmNotifyAll) : 'Are you sure you want to run rules for all organisations?';
                                    if (!window.confirm(msg)) return;
                                    // Enqueue notify (all organisations)
                                    try {
                                        await new Promise((resolve, reject) => {
                                            try {
                                                Relay.Store.commitUpdate(
                                                    new EnqueueOverdueBillsNotifyJob({}),
                                                    { onFailure: (_, err) => reject(err), onSuccess: (response) => {
                                                        try {
                                                            const job = response && response.enqueueOverdueBillsNotifyJob && response.enqueueOverdueBillsNotifyJob.job;
                                                            if (job) showJobNotification(this.props.intl, job, 'Running notify for ALL organisations…');
                                                        } catch (e) {}
                                                        resolve(null);
                                                    } }
                                                );
                                            } catch (e) { reject(e); }
                                        });
                                    } catch (e) { showNotification('Chyba', String(e && e.message ? e.message : e)); }
                                }}>
                                    <FormattedMessage defaultMessage="Run rules (all org.)" id="overdueBills.notifyAll" />
                                </LoadingButton>
                                </span>
                                ) : null}
                            </div>
                        </div>
                    </div>
                    {loading && <div>Loading...</div>}
                    {error && <div style={{color: 'red'}}>{String(error)}</div>}
                    <div className="row" style={{ marginBottom: '10px' }}>
                        <div className="col-md-12">
                            <label className="checkbox-inline">
                                <input
                                    checked={!!this.state.onlyUnpaidOverdue}
                                    onChange={(e) => this.setState({ onlyUnpaidOverdue: !!e.target.checked })}
                                    style={{ marginRight: 6 }}
                                    type="checkbox"
                                />
                                <FormattedMessage {...messages.filterOnlyUnpaidOverdue} />
                            </label>
                            <div className="pull-right">
                                <label className="control-label" style={{ marginRight: 6 }}>
                                    <FormattedMessage {...messages.pageSizeLabel} />:
                                </label>
                                <select
                                    className="form-control input-sm"
                                    onChange={(e) => {
                                        const val = parseInt(e.target.value, 10);
                                        this.setState({ pageSize: isNaN(val) ? 50 : val, currentPage: 1 });
                                    }}
                                    style={{ display: 'inline-block', width: 80 }}
                                    value={String(this.state.pageSize)}
                                >
                                    <option value="1">1</option>
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    {this.state.debug && (
                        <div style={{margin:'8px 0', padding:8, background:'#f8f8f8', border:'1px solid #eee'}}>
                            <div><strong>Debug URL:</strong> <code>{String(this.state.debug.urlPart || '')}</code></div>
                            <details style={{marginTop:6}}>
                                <summary>Raw response</summary>
                                {this.state.debug.raw ? (
                                    <pre style={{whiteSpace:'pre-wrap', wordBreak:'break-all'}}>{JSON.stringify(this.state.debug.raw, null, 2)}</pre>
                                ) : null}
                                {this.state.debug.rawText != null ? (
                                    <pre style={{whiteSpace:'pre-wrap', wordBreak:'break-all'}}>{
                                        typeof this.state.debug.rawText === 'string'
                                            ? this.state.debug.rawText
                                            : JSON.stringify(this.state.debug.rawText, null, 2)
                                    }</pre>
                                ) : null}
                            </details>
                        </div>
                    )}
                    <div className="row">
                        <div className="col-md-12">
                            <Table bordered hover responsive striped>
                                <thead>
                                    <tr>
										<th style={{width: 28, textAlign:'center'}}>
											<input
												checked={visibleItems.length > 0 && visibleItems.every(it => this.state.selectedIds[it.id])}
												onChange={(e) => this.handleToggleSelectAll(visibleItems, !!e.target.checked)}
												type="checkbox"
											/>
										</th>
                                        <th><FormattedMessage {...messages.columnCode} /></th>
                                        <th><FormattedMessage {...messages.columnVariableSymbol} /></th>
                                        <th><FormattedMessage {...messages.columnDueDate} /></th>
                                        <th style={{textAlign:'right'}}><FormattedMessage {...messages.columnTotalAmount} /></th>
                                        <th style={{textAlign:'right'}}><FormattedMessage {...messages.columnAmountToPay} /></th>
                                        <th><FormattedMessage {...messages.columnCurrency} /></th>
                                        <th><FormattedMessage {...messages.columnDaysOverdue} /></th>
                                        <th><FormattedMessage {...messages.columnDocuments} /></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleItems.map((i) => this.renderRow(i))}
                                </tbody>
                            </Table>
                            {isDeveloper ? (
                                <div className="pull-left" style={{ marginTop: 10 }}>
                                    <button
                                        className="btn btn-danger btn-sm"
                                        disabled={selectedCount === 0}
                                        onClick={() => this.handleBulkDelete(items)}
                                    >
                                        <FormattedMessage {...messages.deleteSelected} />
                                    </button>
                                    {' '}
                                    <button
                                        className="btn btn-default btn-sm"
                                        disabled={selectedCount === 0}
                                        onClick={() => this.handleBulkIgnore(items)}
                                    >
                                        <FormattedMessage {...messages.ignoreSelected} />
                                    </button>
                                    {' '}
                                    <button
                                        className="btn btn-default btn-sm"
                                        disabled={selectedCount === 0}
                                        onClick={() => this.handleBulkUnignore(items)}
                                    >
                                        <FormattedMessage {...messages.unignoreSelected} />
                                    </button>
                                </div>
                            ) : null}
							<div className="pull-right" style={{ marginTop: 10 }}>
								<div className="btn-group">
									<button className="btn btn-default btn-sm" disabled={currentPage <= 1} onClick={() => this.setState({ currentPage: currentPage - 1 })}>{'«'}</button>
									<span className="btn btn-default btn-sm disabled">{currentPage} / {totalPages}</span>
									<button className="btn btn-default btn-sm" disabled={currentPage >= totalPages} onClick={() => this.setState({ currentPage: currentPage + 1 })}>{'»'}</button>
								</div>
							</div>
                        </div>
                    </div>
                </div>
            </AppPanel>
        );
    }
}

export default injectIntl(OverdueBillsList);
